import React, { useRef, useState } from "react";
import {
    BackgroundPopoverHeader,
    BackgroundPopover,
    BackgroundPopoverText,
    BackgroundPopoverTitle,
    BackgroundUploadInput,
    CustomEffectRemoveButton,
    CustomEffectTile,
    EffectButton,
    EffectGrid
} from "src/scenes/Chat/components/ChatCallPanel.styled";
import {
    BACKGROUND_EFFECTS,
    CUSTOM_BACKGROUND_SELECTED_KEY,
    getStoredCustomBackgrounds,
    LEGACY_CUSTOM_BACKGROUND_STORAGE_KEY,
    saveStoredCustomBackgrounds
} from "./ChatCallPanel.constants";

const BackgroundEffectsPicker = ({
    customBackgroundUrl = "",
    isApplying,
    onCustomBackgroundChange,
    onSelect,
    processingError,
    selectedEffect,
    title = "Backgrounds & Effects"
}) => {
    const [error, setError] = useState("");
    const [customBackgrounds, setCustomBackgrounds] = useState(getStoredCustomBackgrounds);
    const uploadInputRef = useRef(null);

    const selectEffect = (option) => {
        if (isApplying) {
            return;
        }

        if (!option.supported) {
            setError("Background replacement needs browser person-segmentation support before it can be enabled safely.");
            return;
        }

        setError("");
        onSelect(option.value);
    };

    const selectCustomBackground = (background) => {
        if (isApplying) {
            return;
        }

        localStorage.setItem(CUSTOM_BACKGROUND_SELECTED_KEY, background.id);
        localStorage.setItem(LEGACY_CUSTOM_BACKGROUND_STORAGE_KEY, background.url);
        onCustomBackgroundChange?.(background.url);
        onSelect("custom");
        setError("");
    };

    const handleUpload = (event) => {
        const [file] = event.target.files || [];
        if (!file) {
            return;
        }

        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            setError("Use a JPG, PNG, or WebP image for a custom background.");
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setError("Choose an image smaller than 2 MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = typeof reader.result === "string" ? reader.result : "";
            if (!dataUrl) {
                setError("Unable to read that image.");
                return;
            }

            const nextBackground = {
                id: window.crypto?.randomUUID?.() || `custom-background-${Date.now()}`,
                label: file.name.replace(/\.[^.]+$/, "").slice(0, 24) || `Custom ${customBackgrounds.length + 1}`,
                url: dataUrl
            };
            const nextBackgrounds = [...customBackgrounds, nextBackground];

            setCustomBackgrounds(nextBackgrounds);
            saveStoredCustomBackgrounds(nextBackgrounds);
            selectCustomBackground(nextBackground);
            setError("");
        };
        reader.readAsDataURL(file);
        event.target.value = "";
    };

    const removeCustomBackground = (backgroundToRemove) => {
        const nextBackgrounds = customBackgrounds.filter(background => background.id !== backgroundToRemove?.id);
        const removingSelectedBackground = backgroundToRemove?.url === customBackgroundUrl;

        setCustomBackgrounds(nextBackgrounds);
        saveStoredCustomBackgrounds(nextBackgrounds);

        if (removingSelectedBackground) {
            const nextSelectedBackground = nextBackgrounds[0] || null;
            if (nextSelectedBackground) {
                selectCustomBackground(nextSelectedBackground);
            } else {
                localStorage.removeItem(CUSTOM_BACKGROUND_SELECTED_KEY);
                localStorage.removeItem(LEGACY_CUSTOM_BACKGROUND_STORAGE_KEY);
                onCustomBackgroundChange?.("");
                onSelect("none");
            }
        }

        setError("");
    };

    return (
        <BackgroundPopover aria-busy={Boolean(isApplying)}>
            <BackgroundPopoverHeader>
                <BackgroundPopoverTitle>{title}</BackgroundPopoverTitle>
            </BackgroundPopoverHeader>
            <BackgroundPopoverText>
                Choose no effect, blur, a preset background, or a custom background for this call.
            </BackgroundPopoverText>
            <EffectGrid>
                {BACKGROUND_EFFECTS.map(option => (
                    <EffectButton
                        key={option.value}
                        type="button"
                        disabled={!option.supported || isApplying}
                        $active={selectedEffect === option.value}
                        $thumbnail={option.thumbnail}
                        title={option.description}
                        onClick={() => selectEffect(option)}
                    >
                        <span>{selectedEffect === option.value ? "✓ " : ""}{option.label}</span>
                    </EffectButton>
                ))}
                {customBackgrounds.map((background, index) => (
                    <CustomEffectTile key={background.id}>
                        <EffectButton
                            type="button"
                            disabled={isApplying}
                            $active={selectedEffect === "custom" && customBackgroundUrl === background.url}
                            $thumbnail={`url("${background.url}")`}
                            title={`Use ${background.label || `Custom ${index + 1}`}`}
                            onClick={() => selectCustomBackground(background)}
                        >
                            <span>
                                {selectedEffect === "custom" && customBackgroundUrl === background.url ? "✓ " : ""}
                                {background.label || `Custom ${index + 1}`}
                            </span>
                        </EffectButton>
                        <CustomEffectRemoveButton
                            type="button"
                            disabled={isApplying}
                            aria-label={`Remove ${background.label || `Custom ${index + 1}`}`}
                            title={`Remove ${background.label || `Custom ${index + 1}`}`}
                            onClick={() => removeCustomBackground(background)}
                        >
                            ×
                        </CustomEffectRemoveButton>
                    </CustomEffectTile>
                ))}
                <EffectButton
                    type="button"
                    disabled={isApplying}
                    title="Add custom background"
                    onClick={() => uploadInputRef.current?.click()}
                >
                    <span>Add Background</span>
                </EffectButton>
            </EffectGrid>
            <BackgroundUploadInput ref={uploadInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleUpload} />
            {isApplying && <BackgroundPopoverText role="status">Applying background effect…</BackgroundPopoverText>}
            {!isApplying && processingError && <BackgroundPopoverText role="alert">{processingError}</BackgroundPopoverText>}
            {error && <BackgroundPopoverText>{error}</BackgroundPopoverText>}
        </BackgroundPopover>
    );
};

export default BackgroundEffectsPicker;
